import { supabase } from './supabase';

// ============================================
// STAFF QR + PIN AUTHENTICATION SERVICE
// ============================================

export const staffAuthService = {
  // Verify QR code and get staff info
  async verifyQRCode(qrCodeIdentifier) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, role, qr_code_identifier, is_active')?.eq('qr_code_identifier', qrCodeIdentifier)?.in('role', ['staff', 'trainer', 'manager'])?.eq('is_active', true)?.single();

      if (error || !data) {
        return { 
          data: null, 
          error: { message: 'Invalid QR code or inactive account' } 
        };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Validate PIN using database function
  async validatePIN(qrCodeIdentifier, pin) {
    try {
      const { data, error } = await supabase?.rpc('validate_staff_pin', {
          p_qr_identifier: qrCodeIdentifier,
          p_pin: pin
        });

      if (error) {
        return { data: null, error };
      }

      // RPC returns array, get first result
      const result = data?.[0];
      
      if (!result?.success) {
        return { 
          data: null, 
          error: { message: result?.message || 'PIN validation failed' } 
        };
      }

      return { 
        data: {
          staffId: result?.staff_id,
          role: result?.staff_role,
          name: result?.staff_name,
          success: true
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Log authentication attempt
  async logAuthAttempt(logData) {
    try {
      const { data, error } = await supabase?.from('staff_auth_log')?.insert([{
          staff_id: logData?.staffId,
          gym_id: logData?.gymId,
          auth_method: logData?.authMethod || 'qr_pin',
          qr_code_scanned: logData?.qrCodeScanned,
          pin_verified: logData?.pinVerified || false,
          auth_status: logData?.authStatus,
          ip_address: logData?.ipAddress,
          user_agent: logData?.userAgent
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Set or update staff PIN
  async setStaffPIN(staffId, newPin) {
    try {
      // Validate PIN format (6 digits)
      if (!/^\d{6}$/?.test(newPin)) {
        return { 
          data: null, 
          error: { message: 'PIN must be exactly 6 digits' } 
        };
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({
          staff_pin: newPin,
          pin_last_updated: new Date()?.toISOString(),
          failed_pin_attempts: 0,
          pin_locked_until: null
        })?.eq('id', staffId)?.in('role', ['staff', 'trainer', 'manager'])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get staff QR code identifier
  async getStaffQRCode(staffId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('qr_code_identifier, full_name, role')?.eq('id', staffId)?.in('role', ['staff', 'trainer', 'manager'])?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Check if staff has PIN set
  async hasPINSet(staffId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('staff_pin')?.eq('id', staffId)?.single();

      if (error) {
        return { data: false, error };
      }

      return { data: !!data?.staff_pin, error: null };
    } catch (error) {
      return { data: false, error };
    }
  },

  // Get authentication history
  async getAuthHistory(staffId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('staff_auth_log')?.select('*')?.eq('staff_id', staffId)?.order('created_at', { ascending: false })?.limit(limit);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Clock in with QR + PIN authentication
  async clockInWithQRPIN(qrCodeIdentifier, pin, gymId, location) {
    try {
      // First validate PIN
      const pinValidation = await this.validatePIN(qrCodeIdentifier, pin);
      
      if (pinValidation?.error || !pinValidation?.data?.success) {
        // Log failed attempt
        await this.logAuthAttempt({
          staffId: null,
          gymId,
          authMethod: 'qr_pin',
          qrCodeScanned: qrCodeIdentifier,
          pinVerified: false,
          authStatus: 'failed_pin'
        });
        
        return pinValidation;
      }

      const staffId = pinValidation?.data?.staffId;

      // Log successful authentication
      await this.logAuthAttempt({
        staffId,
        gymId,
        authMethod: 'qr_pin',
        qrCodeScanned: qrCodeIdentifier,
        pinVerified: true,
        authStatus: 'success'
      });

      // Create attendance record
      const { data: attendanceData, error: attendanceError } = await supabase?.from('staff_attendance')?.insert([{
          gym_id: gymId,
          staff_id: staffId,
          attendance_date: new Date()?.toISOString()?.split('T')?.[0],
          clock_in_time: new Date()?.toISOString(),
          status: 'clocked_in',
          location: location || 'QR Scan',
          notes: 'Authenticated via QR + PIN'
        }])?.select()?.single();

      if (attendanceError) {
        return { data: null, error: attendanceError };
      }

      return { 
        data: {
          ...pinValidation?.data,
          attendance: attendanceData
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};

export default staffAuthService;